import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  AUFTRAG_STATUS_BADGE,
  AUFTRAG_STATUS_LABEL,
  type Auftrag,
  type AuftragStatus,
} from '../lib/auftraege-api';
import {
  MANGEL_KATEGORIEN,
  MANGEL_KATEGORIE_LABEL,
  computeSlaInfo,
  reklamationenApi,
  type MangelKategorie,
} from '../lib/reklamationen-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/reklamationen/')({
  beforeLoad: () => requireAccess('/api/v1/reklamationen'),
  component: ReklamationenIndexPage,
});

const STATUS_FILTER: Array<{ value: '' | AuftragStatus; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'entwurf', label: 'Entwurf' },
  { value: 'bestaetigt', label: 'Bestätigt' },
  { value: 'in_arbeit', label: 'In Arbeit' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
];

const KATEGORIE_FILTER: Array<{ value: '' | MangelKategorie; label: string }> = [
  { value: '', label: 'Alle Kategorien' },
  ...MANGEL_KATEGORIEN.map((k) => ({ value: k, label: MANGEL_KATEGORIE_LABEL[k] })),
];

export function ReklamationenIndexPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<'' | AuftragStatus>('');
  const [katFilter, setKatFilter] = useState<'' | MangelKategorie>('');

  const reklamationenQ = useQuery({
    queryKey: ['reklamationen', 'list'],
    queryFn: () => reklamationenApi.list(),
  });

  const items = reklamationenQ.data?.items ?? [];
  const filtered = items.filter(
    (r) =>
      (statusFilter === '' || r.status === statusFilter) &&
      (katFilter === '' || r.mangelKategorie === katFilter),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reklamationen"
        description="Mängel-Tickets gegen Subunternehmer — mit Gewährleistungsfrist-Wecker."
        actions={
          <Link
            to="/reklamationen/neu"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Neue Reklamation
          </Link>
        }
      />

      {reklamationenQ.error ? <ErrorBanner error={reklamationenQ.error as Error} /> : null}

      <div className="flex flex-wrap gap-3">
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Status</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | AuftragStatus)}
          >
            {STATUS_FILTER.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Mangel-Kategorie</span>
          <Select
            value={katFilter}
            onChange={(e) => setKatFilter(e.target.value as '' | MangelKategorie)}
          >
            {KATEGORIE_FILTER.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {reklamationenQ.isLoading ? (
        <SkeletonList />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Keine Reklamationen in dieser Auswahl.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((r) => (
            <ReklamationCard key={r.id} reklamation={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ReklamationCard({ reklamation }: { reklamation: Auftrag }): JSX.Element {
  const sla = computeSlaInfo(reklamation.gewaehrleistungBis);
  const isOpen =
    reklamation.status === 'entwurf' ||
    reklamation.status === 'bestaetigt' ||
    reklamation.status === 'in_arbeit';

  return (
    <li>
      <Link
        to="/reklamationen/$id"
        params={{ id: reklamation.id }}
        className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-medium text-slate-900">{reklamation.titel}</div>
            <div className="mt-1 text-xs text-slate-500">
              {reklamation.mangelKategorie
                ? MANGEL_KATEGORIE_LABEL[reklamation.mangelKategorie as MangelKategorie]
                : 'Kategorie ?'}{' '}
              · gemeldet {formatGermanDateFromTimestamp(reklamation.createdAt)}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${AUFTRAG_STATUS_BADGE[reklamation.status]}`}
          >
            {AUFTRAG_STATUS_LABEL[reklamation.status]}
          </span>
        </div>
        {sla && sla.level !== 'keiner' && isOpen ? (
          <div
            className={`mt-3 inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-medium ${
              sla.level === 'rot'
                ? 'border-rose-300 bg-rose-50 text-rose-900'
                : 'border-amber-300 bg-amber-50 text-amber-900'
            }`}
          >
            Gewährleistung läuft in {sla.daysLeft} Tag{sla.daysLeft === 1 ? '' : 'en'} ab
          </div>
        ) : null}
      </Link>
    </li>
  );
}
